import { useState } from 'react';
import type { HealthScore } from '../../types/health';
import { getScoreRange, CATEGORY_INFO } from '../../constants/health';
import { useNavigate } from 'react-router-dom';
import AnimatedScoreMeter from '../interactive/AnimatedScoreMeter';
import InteractiveCategoryCard from '../interactive/InteractiveCategoryCard';

interface ScoreDashboardProps {
  score: HealthScore;
  dataCompleteness: number;
}

export default function ScoreDashboard({ score, dataCompleteness }: ScoreDashboardProps) {
  const navigate = useNavigate();
  const scoreRange = getScoreRange(score.overall);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [showScoringLogic, setShowScoringLogic] = useState(false);

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset your profile? All your health data will be deleted and you\'ll start fresh.')) {
      localStorage.removeItem('healthProfile');
      navigate('/onboarding');
    }
  };

  // Category weights for display
  const categoryWeights = {
    cardiovascular: 23,
    metabolic: 27,
    lifestyle: 25,
    recovery: 15,
    mental: 10
  };

  // Prepare category data for InteractiveCategoryCard
  const categoryExplanations = {
    cardiovascular: {
      title: 'Cardiovascular Health',
      description: 'Heart health, fitness, and blood circulation',
      icon: 'ri-heart-pulse-line',
      color: '#ef4444',
      factors: [
        'Smoking status and tobacco use',
        'Physical activity and cardio exercise',
        'Blood pressure readings',
        'Resting heart rate',
        'Aerobic fitness level'
      ],
      tips: [
        '150+ minutes of moderate cardio weekly',
        'Quit smoking for major improvement',
        'Maintain optimal blood pressure',
        'Track your resting heart rate'
      ]
    },
    metabolic: {
      title: 'Metabolic Health',
      description: 'Weight management, nutrition, and metabolism',
      icon: 'ri-pulse-line',
      color: '#8b5cf6',
      factors: [
        'Body Mass Index (BMI)',
        'Blood sugar levels',
        'Nutrition quality',
        'Processed food intake',
        'Fruit and vegetable consumption'
      ],
      tips: [
        'Achieve and maintain healthy BMI',
        'Eat 5+ servings of fruits/vegetables daily',
        'Reduce processed food intake',
        'Monitor blood sugar levels'
      ]
    },
    lifestyle: {
      title: 'Lifestyle Habits',
      description: 'Daily activities, habits, and routine choices',
      icon: 'ri-run-line',
      color: '#14b8a6',
      factors: [
        'Alcohol consumption patterns',
        'Daily step count and movement',
        'Strength training frequency',
        'Sedentary time',
        'Hydration habits'
      ],
      tips: [
        'Aim for 10,000+ daily steps',
        'Strength train 2+ days per week',
        'Moderate or eliminate alcohol',
        'Stay well hydrated throughout the day'
      ]
    },
    recovery: {
      title: 'Recovery & Sleep',
      description: 'Sleep quality, duration, and rest patterns',
      icon: 'ri-moon-line',
      color: '#6366f1',
      factors: [
        'Sleep duration (7-9 hours)',
        'Sleep quality rating',
        'Sleep schedule consistency',
        'Recovery time between activities',
        'Rest day frequency'
      ],
      tips: [
        'Get 7-9 hours of quality sleep',
        'Maintain consistent sleep schedule',
        'Create relaxing bedtime routine',
        'Take adequate rest days'
      ]
    },
    mental: {
      title: 'Mental Wellbeing',
      description: 'Stress levels, emotional health, and balance',
      icon: 'ri-mental-health-line',
      color: '#ec4899',
      factors: [
        'Perceived stress levels',
        'Stress management practices',
        'Work-life balance',
        'Social connections',
        'Mindfulness activities'
      ],
      tips: [
        'Practice active stress management',
        'Maintain good work-life balance',
        'Stay socially connected',
        'Try meditation or mindfulness'
      ]
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Main Score Display */}
      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 transform transition-all duration-300 hover:shadow-lg">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="relative group">
            <AnimatedScoreMeter score={score.overall} maxScore={1000} size="large" />
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-4 shadow-sm">
              <div className={`w-3 h-3 rounded-full ${scoreRange.color} animate-pulse`}></div>
              <span className="font-semibold text-gray-900">{scoreRange.label}</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Your Health Score</h2>
            <p className="text-lg text-gray-700 mb-6">{scoreRange.description}</p>
            
            <div className="flex items-center gap-3 text-sm mb-6">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-teal-600"></i>
                <span className="text-gray-700">
                  Confidence: <span className="font-semibold capitalize">{score.confidence}</span>
                </span>
              </div>
              <div className="text-gray-400">•</div>
              <div className="text-gray-600">
                Range: {score.confidenceRange.min}-{score.confidenceRange.max}
              </div>
            </div>

            <button
              onClick={() => navigate('/score-explained')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all hover:shadow-md hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-line"></i>
              <span>How is my score calculated?</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scoring Logic Explanation */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-teal-300">
        <button
          onClick={() => setShowScoringLogic(!showScoringLogic)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
              <i className="ri-bar-chart-box-line text-xl text-teal-600"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-gray-900">How Your Score is Calculated</h3>
              <p className="text-sm text-gray-600">Category weights and impact breakdown</p>
            </div>
          </div>
          <i className={`ri-arrow-${showScoringLogic ? 'up' : 'down'}-s-line text-2xl text-gray-400 transition-transform duration-300`}></i>
        </button>
        
        {showScoringLogic && (
          <div className="px-6 pb-6 space-y-4 animate-slideDown">
            <p className="text-sm text-gray-700 mb-4">
              Each category below has a weighted impact on your overall score. Categories with higher weights contribute more to your final score:
            </p>
            <div className="space-y-3">
              {Object.entries(categoryWeights).map(([key, weight]) => {
                const info = CATEGORY_INFO[key as keyof typeof CATEGORY_INFO];
                return (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <i className={`${info.icon} text-lg text-teal-600`}></i>
                        <span className="font-semibold text-gray-900">{info.name}</span>
                      </div>
                      <span className="text-sm font-bold text-teal-600">{weight}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-teal-500 h-2 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${weight}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-600 mt-4 italic">
              * Weights are optimized based on health research and may adjust as more data is collected.
            </p>
          </div>
        )}
      </div>

      {/* Data Completeness */}
      {dataCompleteness < 100 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 transform transition-all duration-300 hover:shadow-md hover:scale-102">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
              <i className="ri-information-line text-xl text-amber-600"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Improve Your Score Accuracy</h3>
              <p className="text-sm text-gray-700 mb-3">
                Your profile is {dataCompleteness}% complete. Add more health data to get a more accurate score and personalized insights.
              </p>
              <div className="w-full bg-amber-200 rounded-full h-2 mb-3">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${dataCompleteness}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-6">Category Breakdown</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(score.categories).map(([key, category]) => {
            const explanation = categoryExplanations[key as keyof typeof categoryExplanations];
            return (
              <InteractiveCategoryCard
                key={key}
                category={explanation}
                score={category.score}
                onHover={setHoveredCategory}
              />
            );
          })}
        </div>
      </div>

      {/* Score Factors */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-6">What's Affecting Your Score</h3>
        <div className="space-y-4">
          {Object.entries(score.categories).map(([categoryKey, category]) => {
            const info = CATEGORY_INFO[categoryKey as keyof typeof CATEGORY_INFO];
            return category.factors.map((factor, index) => (
              <div
                key={`${categoryKey}-${index}`}
                className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    factor.impact === 'positive' ? 'bg-green-100' : 
                    factor.impact === 'negative' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <i className={`text-xl ${
                      factor.impact === 'positive' ? 'ri-arrow-up-line text-green-600' :
                      factor.impact === 'negative' ? 'ri-arrow-down-line text-red-600' :
                      'ri-subtract-line text-gray-600'
                    }`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-gray-500 uppercase">{info.name}</span>
                      <span className={`text-sm font-bold ${
                        factor.impact === 'positive' ? 'text-green-600' :
                        factor.impact === 'negative' ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {factor.value > 0 ? '+' : ''}{factor.value}
                      </span>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{factor.name}</h4>
                    <p className="text-sm text-gray-600">{factor.description}</p>
                  </div>
                </div>
              </div>
            ));
          })}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-xl text-gray-600"></i>
          </div>
          <div className="text-sm text-gray-700">
            <strong>Important Disclaimer:</strong> HealthScore provides informational insights only and is not intended to diagnose, treat, or replace professional medical advice. This score cannot be used for insurance, employment, or any discriminatory purposes.
          </div>
        </div>
      </div>

      {/* Reset Button */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 mb-2">Start Over</h3>
            <p className="text-sm text-gray-600">Reset your profile and re-enter your information from scratch</p>
          </div>
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-red-50 border-2 border-red-200 text-red-700 rounded-lg font-semibold hover:bg-red-100 hover:border-red-300 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-restart-line mr-2"></i>
            Reset & Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
